import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AbMarkdownEditor } from "abreact/input/AbMarkdownEditor";
import { AbButton } from "abreact/misc/AbButton";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import { AbSize } from "abreact/misc/AbSize";
import { AbSplitButton } from "abreact/misc/AbSplitButton";
import * as React from "react";
function isDark(color) {
  if (["var(--ab-global-color-black)", "var(--ab-global-color-pink-700)", "black", "red", "green", "blue"].includes(color)) {
    return true;
  }
  const matches = color.match(/--ab-global-color-\w*-(\d*)/);
  if (matches) {
    if (["700", "800", "900"].includes(matches[1])) {
      return true;
    }
  }
  return false;
}
export function isBlack(color) {
  return color === "black" || color === "var(--ab-global-color-black)" || color === "#000000";
}
export function isWhite(color) {
  return color === "white" || color === "var(--ab-global-color-white)" || color === "#ffffff";
}
// Helper component: Show one of the 150 buttons inside the Picker
class ColorButton extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(AbButton, {
      dataTestId: `color-button-${this.props.color}`,
      className: "abui",
      style: {
        backgroundColor: this.props.color
      },
      size: AbSize.SMALL,
      tooltip: this.props.color // ojanssen: I'm colorblind and this was helpful for me for configuring a specific color, but if this is annoying to other people we can remove it
      ,
      onClick: () => this.props.onClick(this.props.color)
      // if this is the current color, paint a checkmark. If the color is dark, make it a white checkmark.
      ,
      icon: this.props.color === this.props.currentColor ? /*#__PURE__*/React.createElement(AbIcon, {
        image: "absdk:check",
        className: isDark(this.props.color) ? "ab-reverse" : ""
      }) : undefined
    });
  }
}
export class AbColorPicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "popupButtonRef", /*#__PURE__*/React.createRef());
  }
  // Called when user picks a color from the popup.  Changes the currentColor, and tells the caller, too.
  newColor(color) {
    this.popupButtonRef.current?.close();
    this.props.onChange(color);
  }

  // One array entry for each "standard" color

  static addColorsToMarkdownOptions(options) {
    AbColorPicker.simpleColors.map(x => {
      options.styleItems["COLOR_" + x] = {
        open: () => `{color: ${x}}`,
        close: () => "{color}"
      };
      options.styleItems["BACKGROUND_" + x] = {
        open: () => `{background: ${x}}`,
        close: () => "{background}"
      };
    });
    AbColorPicker.abinitioColors.map(row => ["050", "100", "200", "300", "400", "500", "600", "700", "800", "900"].map(column => {
      options.styleItems[`COLOR_var(--ab-global-color-${row}-${column})`] = {
        open: () => `{color: ${row}-${column}}`,
        close: () => "{color}"
      };
      options.styleItems[`BACKGROUND_var(--ab-global-color-${row}-${column})`] = {
        open: () => `{background: ${row}-${column}}`,
        close: () => "{background}"
      };
    }));
    AbColorPicker.simpleColors.map(x => {
      AbMarkdownEditor.markdownToDraftBlockStyles["color_" + x + "_open"] = "COLOR_" + x;
      AbMarkdownEditor.markdownToDraftBlockStyles["background_" + x + "_open"] = "BACKGROUND_" + x;
    });
    AbColorPicker.abinitioColors.map(row => ["050", "100", "200", "300", "400", "500", "600", "700", "800", "900"].map(column => {
      AbMarkdownEditor.markdownToDraftBlockStyles[`color_${row}-${column}_open`] = `COLOR_var(--ab-global-color-${row}-${column})`;
      AbMarkdownEditor.markdownToDraftBlockStyles[`background_${row}-${column}_open`] = `BACKGROUND_var(--ab-global-color-${row}-${column})`;
    }));
  }
  static getColorTokens() {
    let retVal = [];
    AbColorPicker.simpleColors.forEach(x => {
      retVal.push(`color_${x}_open`);
      retVal.push(`color_${x}_close`);
      retVal.push(`background_${x}_open`);
      retVal.push(`background_${x}_close`);
    });
    AbColorPicker.abinitioColors.forEach(row => ["050", "100", "200", "300", "400", "500", "600", "700", "800", "900"].forEach(column => {
      retVal.push(`color_${row}-${column}_open`);
      retVal.push(`color_${row}-${column}_close`);
      retVal.push(`background_${row}-${column}_open`);
      retVal.push(`background_${row}-${column}_close`);
    }));
    return retVal;
  }
  render() {
    if (!this.props) {
      return null;
    } // too early
    // If the color is black and showBlackBackgroundColor is false, or this component is disabled and
    // showBackgroundColorOnDisabled is false don't set any color.
    let bgColor = this.props.value;
    const bgColorIsBlack = bgColor === "var(--ab-global-color-black)" || bgColor === "black";
    if (!bgColor || bgColorIsBlack && !this.props.showBlackBackgroundColor || this.props.disabled && !this.props.showBackgroundColorOnDisabled) {
      bgColor = "unset";
    }

    // TODO add icon for background color being undefined
    return /*#__PURE__*/React.createElement(AbSplitButton, {
      className: CssUtils.composeClassNames("ab-markdown-menu-button", this.props.className)
    }, /*#__PURE__*/React.createElement(AbButton, {
      icon: this.props.icon && /*#__PURE__*/React.createElement("div", {
        className: "ab-color-picker-icon-background",
        "data-test-id": `color-picker-icon-button-${bgColor}`,
        style: {
          backgroundColor: bgColor
        }
      }, /*#__PURE__*/React.createElement(AbIcon, {
        image: this.props.icon
      })),
      label: this.props.label,
      disabled: this.props.disabled,
      onClick: () => this.props.value && this.props.onChange(this.props.value),
      className: CssUtils.composeClassNames("ab-no-border-button", !this.props.label && "ab-square-button"),
      tooltip: this.props.tooltip
    }), /*#__PURE__*/React.createElement(AbPopupButton, {
      icon: "absdk:chevron-down",
      abRef: this.popupButtonRef,
      tooltip: this.props.tooltip,
      className: "ab-no-border-button",
      popupContents: /*#__PURE__*/React.createElement(React.Fragment, null, this.props.show12Colors && /*#__PURE__*/React.createElement("div", {
        className: "ab-color-picker-simple-grid"
      }, AbColorPicker.twelveColors.map(x => {
        const renderedColor = `var(--ab-global-color-${x})`;
        return /*#__PURE__*/React.createElement(ColorButton, {
          color: renderedColor,
          key: x,
          onClick: this.newColor.bind(this),
          currentColor: this.props.value
        });
      })), !this.props.show12Colors && /*#__PURE__*/React.createElement("div", {
        className: "ab-color-picker-grid"
      }, AbColorPicker.simpleColors.map(x => /*#__PURE__*/React.createElement(ColorButton, {
        color: x,
        key: x,
        onClick: this.newColor.bind(this),
        currentColor: this.props.value
      })), /*#__PURE__*/React.createElement("div", {
        className: "spacer-below-standard-colors"
      }), [...AbColorPicker.abinitioColors.map(row => ["100", "200", "300", "400", "500", "600", "700", "800", "900"].map(column => /*#__PURE__*/React.createElement(ColorButton, {
        color: `var(--ab-global-color-${row}-${column})`,
        key: `${row}-${column}`,
        onClick: this.newColor.bind(this),
        currentColor: this.props.value
      })))]))
    }));
  }
}
_defineProperty(AbColorPicker, "simpleColors", ["white", "black", "red", "orange", "yellow", "green", "cyan", "blue", "magenta"]);
// One color for each "row" of colors in abinitio-color-palette.css
_defineProperty(AbColorPicker, "abinitioColors", ["steel", "red", "redorange", "orange", "yellow", "yellowgreen", "green", "aqua", "blue", "indigo", "pink", "purple", "brown"]);
_defineProperty(AbColorPicker, "twelveColors", ["black", "white", "steel-500", "red-600", "orange-400", "green-500", "blue-600", "purple-500", "yellow-300", "aqua-300", "steel-200", "green-200"]);
//# sourceMappingURL=AbColorPicker.js.map
